import _, { unionBy } from 'lodash';
import BatteryAlertIcon from '@material-ui/icons/BatteryAlert';
import { plural, t } from '@lingui/macro';
import { COLUMN_TYPES } from '../../data-table/DataTableColumns';
import {
    AGENT_ASN_FIELD,
    AGENT_BEACONS_TRANSLATED_FIELD,
    AGENT_CREATED_AT_FIELD,
    AGENT_GEO_ISP_CITY_FIELD,
    AGENT_GEO_ISP_COUNTRY_FIELD,
    AGENT_GEO_ISP_LATITUDE_FIELD,
    AGENT_GEO_ISP_LONGITUDE_FIELD,
    AGENT_GEO_ISP_NETWORK_FIELD,
    AGENT_GEO_ISP_POSTAL_FIELD,
    AGENT_GEO_ISP_PROVIDER_FIELD,
    AGENT_GEO_ISP_SUB_DIVISION_ISO_CODE_FIELD,
    AGENT_GEO_ISP_SUBDIVISION_FIELD,
    AGENT_LAST_SEEN_AT_FIELD,
    AGENT_MODEL_SHORT_DISPLAY_FIELD,
    AGENT_NAME_FIELD,
    AGENT_NEAREST_SWITCH_FIELD,
    AGENT_NEAREST_SWITCH_IP_FIELD,
    AGENT_NEAREST_SWITCH_IP_SORT_FIELD,
    AGENT_NEAREST_SWITCH_PORT_FIELD,
    AGENT_NETWORK_HOSTNAME_FIELD,
    AGENT_NETWORK_IP_EXTERNAL_FIELD,
    AGENT_NETWORK_IP_EXTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_INTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV4_LOCAL_FIELD,
    AGENT_NETWORK_IPV6_INTERNAL_SORT_FIELD,
    AGENT_NETWORK_IPV6_LOCAL_FIELD,
    AGENT_NETWORK_MAC_FIELD,
    AGENT_SERIAL_NUMBER,
    AGENT_NETWORK_WIFI_MAC_FIELD,
    AGENT_PUBLIC_IP_FIELD,
    AGENT_SITES_LIST_FIELD,
    AGENT_SITES_LIST_SORTABLE_FIELD,
    AGENT_SOFTWARE_VERSION_FIELD,
    AGENT_STATUS_FIELD,
    AGENT_STATUS_UPDATE,
    AGENT_TAGS_DISPLAY_FIELD,
    AGENT_TEST_COUNT_DISPLAY_FIELD,
    AGENT_TEST_COUNT_FIELD,
    AGENT_TRANSLATED_TYPE_FIELD,
    AGENT_TRANSLATED_WEB_PROXY_FIELD,
    AGENT_TYPE_ICON_FIELD,
    AGENT_UPDATE_ACTION_STATUSES,
    AGENT_VITALS_CERT_VALID_BEFORE,
    AGENT_VITALS_POE_VOLTAGE,
    AGENT_WIFI_SSID_FIELD,
    AGENTS_FUNCTION_NAME,
} from './AgentConstants';
import { LegacyIcon } from '../../icon';
import { buildPageTarget } from '../AdminLinkUtils';
import {
    renderCertDate,
    renderCertTooltip,
    renderMatchingSites,
    renderStatus,
    renderTestCount,
    renderVersion,
    renderWifiSSID,
} from './AgentRenderHelper';
import { dateTimeFormatter, FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { POE_WARNING_CLASS } from './AgentStyles';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../stores/AlertsStore';
import { isHardware, isHardware3k } from './AgentHelper';
import { softwareVersionMapper } from './AgentVersionHelper';
import { formatTags } from '../../../utilities/StringUtilities';

const batteryWarningText = t`This nPoint should be moved to PoE`;
const certificateStatus = t`mTLS Certificate`;

export function buildActionParams(theDataSource, theTableState) {
    return _.merge(
        {
            search: theTableState?.search || '',
        },
        theDataSource.params
    );
}

export function getAgentListColumns(classes) {
    const agentListColumns = [
        {
            type: COLUMN_TYPES.ICON,
            title: t`Type`,
            width: 74,
            mapping: AGENT_TYPE_ICON_FIELD,
            sortable: true,
            csvFormatter: (value, row) => row[AGENT_TRANSLATED_TYPE_FIELD],
            className: classes.root,
            icons: [
                {
                    getIcon: getAgentTypeIcon,
                    getTooltip: getAgentTypeTooltip,
                },
            ],
        },
        {
            type: COLUMN_TYPES.LINK,
            title: t`Name`,
            sortable: true,
            mapping: AGENT_NAME_FIELD,
            width: 300,
            link: {
                target: buildPageTarget(AGENTS_FUNCTION_NAME),
            },
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Model`,
            sortable: true,
            mapping: AGENT_MODEL_SHORT_DISPLAY_FIELD,
            width: 85,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Status`,
            sortable: true,
            mapping: AGENT_STATUS_FIELD,
            width: 105,
            className: classes.root,
            renderer: renderStatus,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: certificateStatus,
            sortable: true,
            mapping: AGENT_VITALS_CERT_VALID_BEFORE,
            className: classes.root,
            formatter: FORMATTER_TYPES.DATE,
            renderer: renderCertDate,
            getTooltip: renderCertTooltip,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Version`,
            sortable: true,
            mapping: AGENT_SOFTWARE_VERSION_FIELD,
            className: classes.root,
            mapper: softwareVersionMapper,
            renderer: renderVersion,
            width: 125,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Service Tests`,
            sortable: true,
            sortProperty: AGENT_TEST_COUNT_FIELD,
            mapping: AGENT_TEST_COUNT_DISPLAY_FIELD,
            className: classes.root,
            renderer: renderTestCount,
            width: 145,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`External IP`,
            sortable: true,
            mapping: AGENT_NETWORK_IP_EXTERNAL_FIELD,
            sortProperty: AGENT_NETWORK_IP_EXTERNAL_SORT_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IPv4`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV4_LOCAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV4_INTERNAL_SORT_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Internal IPv6`,
            sortable: true,
            mapping: AGENT_NETWORK_IPV6_LOCAL_FIELD,
            sortProperty: AGENT_NETWORK_IPV6_INTERNAL_SORT_FIELD,
            width: 300,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Public IP`,
            csvOnly: true,
            mapping: AGENT_PUBLIC_IP_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Hostname`,
            sortable: true,
            mapping: AGENT_NETWORK_HOSTNAME_FIELD,
            width: 175,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Admin MAC`,
            sortable: true,
            mapping: AGENT_NETWORK_MAC_FIELD,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Serial Number`,
            sortable: true,
            mapping: AGENT_SERIAL_NUMBER,
            width: 140,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Smart Edge Monitoring`,
            sortable: true,
            align: 'center',
            mapping: AGENT_BEACONS_TRANSLATED_FIELD,
            width: 190,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Wi-Fi Network`,
            sortable: true,
            mapping: AGENT_WIFI_SSID_FIELD,
            className: classes.root,
            width: 200,
            renderer: renderWifiSSID,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Wi-Fi MAC`,
            sortable: true,
            mapping: AGENT_NETWORK_WIFI_MAC_FIELD,
            width: 130,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`First Seen`,
            align: 'right',
            sortable: true,
            mapping: AGENT_CREATED_AT_FIELD,
            formatter: FORMATTER_TYPES.DURATIONSINCE,
            getTooltip: (row) => dateTimeFormatter(row[AGENT_CREATED_AT_FIELD]),
            width: 135,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Last Seen`,
            align: 'right',
            sortable: true,
            mapping: AGENT_LAST_SEEN_AT_FIELD,
            formatter: FORMATTER_TYPES.DURATIONSINCE,
            getTooltip: (row) => dateTimeFormatter(row[AGENT_LAST_SEEN_AT_FIELD]),
            width: 135,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Sites`,
            mapping: AGENT_SITES_LIST_FIELD,
            sortProperty: AGENT_SITES_LIST_SORTABLE_FIELD,
            sortable: true,
            width: 200,
            className: classes.root,
            csvFormatter: formatTags,
            renderer: renderMatchingSites,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Proxy`,
            sortable: true,
            mapping: AGENT_TRANSLATED_WEB_PROXY_FIELD,
            width: 175,
        },

        {
            type: COLUMN_TYPES.TEXT,
            title: t`Switch Name`,
            sortable: true,
            mapping: AGENT_NEAREST_SWITCH_FIELD,
            width: 235,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Switch IP`,
            sortable: true,
            mapping: AGENT_NEAREST_SWITCH_IP_FIELD,
            sortProperty: AGENT_NEAREST_SWITCH_IP_SORT_FIELD,
            width: 150,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Switch Port`,
            sortable: true,
            mapping: AGENT_NEAREST_SWITCH_PORT_FIELD,
            width: 150,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Tags`,
            sortable: true,
            mapping: AGENT_TAGS_DISPLAY_FIELD,
            width: 200,
        },

        {
            type: COLUMN_TYPES.TEXT,
            title: t`ISP`,
            sortable: true,
            mapping: AGENT_GEO_ISP_PROVIDER_FIELD,
            width: 200,
        },
        {
            type: COLUMN_TYPES.TEXT,
            csvOnly: true,
            title: t`Subdivision Code`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_SUB_DIVISION_ISO_CODE_FIELD,
        },
        {
            type: COLUMN_TYPES.NUMERIC,
            title: t`ASN`,
            csvOnly: true,
            mapping: AGENT_ASN_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`City`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_CITY_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            title: t`Subdivision`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_SUBDIVISION_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            csvOnly: true,
            title: t`Country`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_COUNTRY_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            csvOnly: true,
            title: t`Postal Code`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_POSTAL_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            csvOnly: true,
            title: t`Latitude`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_LATITUDE_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            csvOnly: true,
            title: t`Longitude`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_LONGITUDE_FIELD,
        },
        {
            type: COLUMN_TYPES.TEXT,
            csvOnly: true,
            title: t`Network`,
            sortable: true,
            width: 200,
            mapping: AGENT_GEO_ISP_NETWORK_FIELD,
        },
    ];

    return agentListColumns;
}

export function getAgentListColumnOverrides(stickyState) {
    const { columnOverrides, columnOverridesVersion } = stickyState || {};

    let migratedColumnOverrides = columnOverrides || [];
    let version = columnOverridesVersion;
    if (!columnOverridesVersion) {
        migratedColumnOverrides = unionBy(
            columnOverrides || [],
            [
                {
                    id: certificateStatus,
                    hidden: true,
                },
            ],
            'id'
        );
        version = 1;
    }

    return { columnOverrides: migratedColumnOverrides, columnOverridesVersion: version };
}

export function collectDeleteErrors(deleteErrors) {
    const testNeedsPulseKey = 'test_needs_pulse';
    let genericError = false;
    const errors = [];

    errors.push(
        plural(deleteErrors.length, {
            one: `${deleteErrors.length} nPoint was not deleted because of the following error:`,
            other: `${deleteErrors.length} nPoints were not deleted because of the following error(s):`,
        })
    );

    // If we have more than 2 errors, they won't fit in the message, so trim the message down.
    if (_.filter(deleteErrors, (error) => error && error.message === testNeedsPulseKey).length > 2) {
        errors.push(t`nPoints could not be deleted because they are used by Service Tests`);
        return errors;
    }

    // Loop through and build out our errors.
    _.each(deleteErrors, (error) => {
        if (error && error.message === testNeedsPulseKey) {
            const pulseName = _.get(error, 'pulseName');
            const testNames = _.get(error, 'testNames');
            errors.push(
                t`The nPoint ${pulseName} could not be deleted because it is required for the following Service Test(s): ${testNames}`
            );
        } else if (genericError === false) {
            genericError = true;
            errors.push(
                t`There was a problem communicating with the server.  If this issue persists, please contact support.`
            );
        }
    });

    return errors;
}

export function getAgentTypeIcon(row) {
    if (isRunningOnBattery(row)) {
        return <BatteryAlertIcon title={batteryWarningText} className={POE_WARNING_CLASS} />;
    }
    return <LegacyIcon icon={row[AGENT_TYPE_ICON_FIELD]} />;
}

export function getAgentTypeTooltip(row) {
    if (isRunningOnBattery(row)) {
        return batteryWarningText;
    }

    return row[AGENT_TRANSLATED_TYPE_FIELD];
}

function isRunningOnBattery(agent) {
    const poeThreshold = 25;

    // Not valid for the nPoint 3000
    if (isHardware3k(agent)) {
        return false;
    }

    if (isHardware(agent) && _.get(agent, AGENT_VITALS_POE_VOLTAGE) < poeThreshold) {
        return true;
    }

    return false;
}

function getUpdateAction(action) {
    return AGENT_UPDATE_ACTION_STATUSES[action] || action || AGENT_UPDATE_ACTION_STATUSES[AGENT_STATUS_UPDATE];
}

export function usePostBulkActionHandler() {
    const [, uiAlertActions] = useUiAlerts();
    // Check to see if we had some status update mismatch.
    const bulkActionErrorField = 'invalid_status';

    return (updateObject, response) => {
        const failedAgents = response[bulkActionErrorField];

        if (_.isArray(failedAgents) && failedAgents.length > 0) {
            uiAlertActions.createAlert(
                createAlertConfig(
                    WARNING,
                    t`The following nPoints status does not allow them to be ${getUpdateAction(
                        updateObject.status
                    )}: ${failedAgents.join(', ')}`
                )
            );
        }
        return response;
    };
}
